/**
 * Modern Button component with refined interactions
 */

import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const variants = {
  primary: cn(
    'bg-primary-500 text-white',
    'hover:bg-primary-600 hover:shadow-lg hover:shadow-primary-500/25',
    'active:bg-primary-700',
    'focus-visible:ring-primary-500',
    'dark:bg-primary-600 dark:hover:bg-primary-500'
  ),
  secondary: cn(
    'bg-secondary-500 text-neutral-900',
    'hover:bg-secondary-400 hover:shadow-lg hover:shadow-secondary-500/25',
    'active:bg-secondary-600',
    'focus-visible:ring-secondary-500'
  ),
  outline: cn(
    'border-2 border-neutral-300 dark:border-neutral-600',
    'text-neutral-700 dark:text-neutral-300',
    'hover:border-neutral-400 hover:bg-neutral-50',
    'dark:hover:border-neutral-500 dark:hover:bg-neutral-800',
    'active:bg-neutral-100 dark:active:bg-neutral-700',
    'focus-visible:ring-neutral-500'
  ),
  ghost: cn(
    'text-neutral-600 dark:text-neutral-400',
    'hover:bg-neutral-100 dark:hover:bg-neutral-800',
    'hover:text-neutral-900 dark:hover:text-neutral-100',
    'active:bg-neutral-200 dark:active:bg-neutral-700',
    'focus-visible:ring-neutral-500'
  ),
  danger: cn(
    'bg-red-500 text-white',
    'hover:bg-red-600 hover:shadow-lg hover:shadow-red-500/25',
    'active:bg-red-700',
    'focus-visible:ring-red-500',
    'dark:bg-red-600 dark:hover:bg-red-500'
  ),
  success: cn(
    'bg-green-500 text-white',
    'hover:bg-green-600 hover:shadow-lg hover:shadow-green-500/25',
    'active:bg-green-700',
    'focus-visible:ring-green-500',
    'dark:bg-green-600 dark:hover:bg-green-500'
  ),
};

const sizes = {
  sm: 'px-3 py-1.5 text-sm gap-1.5 rounded-lg',
  md: 'px-4 py-2.5 text-sm gap-2 rounded-xl',
  lg: 'px-6 py-3 text-base gap-2.5 rounded-xl',
  icon: 'p-2.5 rounded-xl',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading,
      icon,
      iconPosition = 'left',
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const isIconOnly = size === 'icon';
    const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';

    return (
      <button
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center font-medium',
          'transition-all duration-200 ease-out',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-900',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
          'active:scale-[0.98]',
          // Variant and size
          variants[variant],
          sizes[size],
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className={cn(iconSize, 'animate-spin')} />
            {!isIconOnly && children && <span>{children}</span>}
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && (
              <span className={cn(iconSize, 'flex-shrink-0')}>{icon}</span>
            )}
            {!isIconOnly && children}
            {icon && iconPosition === 'right' && (
              <span className={cn(iconSize, 'flex-shrink-0')}>{icon}</span>
            )}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
